import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Plane, Plus, Search, X, LayoutList, PanelLeftClose, PanelLeftOpen, Trash2, Pencil,
  Upload, Send, CalendarClock, ExternalLink, ClipboardList, Lock, Star, Paperclip, Undo2, Timer, MessageSquare, AlertTriangle,
} from "lucide-react";

const AC_STATUS = {
  normal: { label: "Serviceable", plane: "text-emerald-500" },
  heavy_check: { label: "Heavy Check", plane: "text-blue-500" },
  mel: { label: "MEL", plane: "text-amber-500" },
  capex: { label: "CAPEX", plane: "text-rose-500" },
  unserviceable: { label: "Unserviceable", plane: "text-red-600" },
  offline: { label: "Offline", plane: "text-gray-400" },
};
const todayIso = () => new Date().toLocaleDateString("en-CA");
const isOverdue = (d) => !!d && d < todayIso();
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

const EMPTY_FORM = { limiter: "", due_date: "", due_hours: "", due_cycles: "", description: "", part_details: "", jobcard_info: "" };

function JobFormDialog({ open, onOpenChange, tail, ac, initial, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const editing = !!initial?.id;

  useEffect(() => {
    if (open) {
      setForm(initial?.id ? {
        limiter: initial.limiter || "",
        due_date: initial.due_date || "",
        due_hours: initial.due_hours ?? "",
        due_cycles: initial.due_cycles ?? "",
        remaining_hours: initial.remaining_hours ?? "",
        remaining_cycles: initial.remaining_cycles ?? "",
        remaining_days: initial.remaining_days ?? "",
        description: initial.description || "",
        part_details: initial.part_details || "",
        jobcard_info: initial.jobcard_info || "",
      } : EMPTY_FORM);
      setAttachments(initial?.id ? (initial.attachments || []) : []);
    }
  }, [open, initial]);

  const uploadFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (attachments.length >= 5) { toast.error("Max 5 attachments"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/planning/files", fd);
      setAttachments((xs) => [...xs, r.data]);
      toast.success(`Attached ${f.name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const lim = (form.limiter || "").toLowerCase();
  const showRemHours = lim.includes("hour");
  const showRemCycles = lim.includes("landing") || lim.includes("cycle");
  const showRemDays = lim.includes("cal") || lim.includes("day");

  const setRemaining = (key, v) => {
    setForm((f) => {
      const next = { ...f, [key]: v };
      const n = v === "" ? null : Number(v);
      if (n != null && !Number.isNaN(n)) {
        if (key === "remaining_hours" && ac?.airframe_hours != null) next.due_hours = String(+(ac.airframe_hours + n).toFixed(1));
        if (key === "remaining_cycles" && ac?.cycles != null) next.due_cycles = String(Math.round(ac.cycles + n));
        if (key === "remaining_days") {
          const d = new Date();
          d.setDate(d.getDate() + Math.round(n));
          next.due_date = d.toLocaleDateString("en-CA");
        }
      }
      return next;
    });
  };

  const save = async () => {
    if (!form.description.trim()) { toast.error("A description is required"); return; }
    setBusy(true);
    const payload = {
      limiter: form.limiter || null,
      due_date: form.due_date || null,
      due_hours: form.due_hours === "" ? null : Number(form.due_hours),
      due_cycles: form.due_cycles === "" ? null : Number(form.due_cycles),
      remaining_hours: form.remaining_hours === "" ? null : Number(form.remaining_hours),
      remaining_cycles: form.remaining_cycles === "" ? null : Number(form.remaining_cycles),
      remaining_days: form.remaining_days === "" ? null : Number(form.remaining_days),
      description: form.description,
      part_details: form.part_details || null,
      jobcard_info: form.jobcard_info || null,
      attachments,
    };
    try {
      if (editing) await api.patch(`/planning/jobs/${initial.id}`, payload);
      else await api.post(`/planning/aircraft/${tail}/jobs`, payload);
      toast.success(editing ? "Job updated" : "Job added to plan");
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save job");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="plan-job-form">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><CalendarClock className="w-4 h-4" /> {editing ? "Edit job" : "New job"}</DialogTitle>
          <DialogDescription>{tail}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-widest text-gray-500">Description *</Label>
            <Textarea className="mt-1 text-sm" rows={3} placeholder="Upcoming work / job…" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="plan-form-desc" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Type of limiter</Label>
              <Input className="mt-1" placeholder="e.g. Calendar, Hours, Landings" value={form.limiter} onChange={(e) => setForm((f) => ({ ...f, limiter: e.target.value }))} data-testid="plan-form-limiter" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Estimated due date</Label>
              <Input className="mt-1" type="date" value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} data-testid="plan-form-due" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Due at hours</Label>
              <Input className="mt-1" type="number" min="0" step="0.1" placeholder="optional" value={form.due_hours} onChange={(e) => setForm((f) => ({ ...f, due_hours: e.target.value }))} data-testid="plan-form-due-hours" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Due at landings</Label>
              <Input className="mt-1" type="number" min="0" step="1" placeholder="optional" value={form.due_cycles} onChange={(e) => setForm((f) => ({ ...f, due_cycles: e.target.value }))} data-testid="plan-form-due-cycles" />
            </div>
            {showRemHours && (
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Remaining Hours</Label>
                <Input className="mt-1" type="number" step="0.1" placeholder="BR — auto-fills due at hours" value={form.remaining_hours} onChange={(e) => setRemaining("remaining_hours", e.target.value)} data-testid="plan-form-rem-hours" />
              </div>
            )}
            {showRemCycles && (
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Remaining Cycles</Label>
                <Input className="mt-1" type="number" step="1" placeholder="BR — auto-fills due at landings" value={form.remaining_cycles} onChange={(e) => setRemaining("remaining_cycles", e.target.value)} data-testid="plan-form-rem-cycles" />
              </div>
            )}
            {showRemDays && (
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Remaining Cal Days</Label>
                <Input className="mt-1" type="number" step="1" placeholder="BR — auto-fills est. due date" value={form.remaining_days} onChange={(e) => setRemaining("remaining_days", e.target.value)} data-testid="plan-form-rem-days" />
              </div>
            )}
          </div>
          {(showRemHours || showRemCycles || showRemDays) && (
            <p className="text-[10px] text-gray-400 -mt-1" data-testid="plan-form-rem-hint">
              Remaining (BR) values auto-calculate the due fields from {tail}&apos;s current{ac?.airframe_hours != null ? ` ${ac.airframe_hours} hrs` : ""}{ac?.cycles != null ? ` / ${ac.cycles} cycles` : ""} and today&apos;s date.
            </p>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Jobcard information</Label>
              <Input className="mt-1" placeholder="Jobcard # / reference" value={form.jobcard_info} onChange={(e) => setForm((f) => ({ ...f, jobcard_info: e.target.value }))} data-testid="plan-form-jobcard" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Part details</Label>
              <Input className="mt-1" placeholder="Part numbers, quantities…" value={form.part_details} onChange={(e) => setForm((f) => ({ ...f, part_details: e.target.value }))} data-testid="plan-form-parts" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-gray-500">Attachments</Label>
            <div className="space-y-1.5 mt-1" data-testid="plan-form-attachments">
              {attachments.map((a) => (
                <div key={a.id} className="flex items-center gap-2 text-xs border border-summit-hairline rounded-md px-2.5 py-1.5 bg-white" data-testid={`plan-form-attachment-${a.id}`}>
                  <Paperclip className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                  <span className="truncate">{a.filename}</span>
                  <span className="text-gray-400 shrink-0">{Math.max(1, Math.round((a.size || 0) / 1024))} KB</span>
                  <button type="button" className="ml-auto p-0.5 rounded text-gray-400 hover:text-rose-600 shrink-0" onClick={() => setAttachments((xs) => xs.filter((x) => x.id !== a.id))} data-testid={`plan-form-attach-del-${a.id}`}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
            <label className={`mt-1.5 inline-flex items-center gap-1.5 text-xs text-summit-primary hover:underline cursor-pointer ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
              <Paperclip className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : "Attach file"}
              <input type="file" className="hidden" onChange={uploadFile} data-testid="plan-form-attach-input" />
            </label>
          </div>
          <Button className="w-full bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={save} disabled={busy} data-testid="plan-form-submit">
            {editing ? "Save changes" : "Add to plan"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ScanDialog({ scan, onClose, tail, onImported }) {
  const [checked, setChecked] = useState({});
  const [busy, setBusy] = useState(false);
  const rows = scan?.rows || [];
  const newRows = rows.filter((r) => !r.already_planned);
  const dupCount = rows.length - newRows.length;

  useEffect(() => {
    if (scan) {
      const all = {};
      (scan.rows || []).forEach((r) => { if (!r.already_planned) all[r.row] = true; });
      setChecked(all);
    }
  }, [scan]);

  const selected = rows.filter((r) => checked[r.row]);
  const allOn = newRows.length > 0 && newRows.every((r) => checked[r.row]);

  const importSelected = async () => {
    if (selected.length === 0) { toast.error("Select at least one row"); return; }
    setBusy(true);
    try {
      const jobs = selected.map((r) => ({
        limiter: r.limiter,
        due_date: r.due_date,
        due_hours: r.due_hours,
        due_cycles: r.due_cycles,
        remaining_hours: r.remaining_hours,
        remaining_cycles: r.remaining_cycles,
        remaining_days: r.remaining_days,
        description: r.description,
        part_details: r.part_details,
        jobcard_info: r.jobcard_info,
      }));
      const { data } = await api.post(`/planning/aircraft/${tail}/jobs/bulk`, { jobs });
      toast.success(`${data.created} job${data.created === 1 ? "" : "s"} added to ${tail}'s plan`);
      onClose();
      onImported();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to import rows");
    } finally {
      setBusy(false);
    }
  };

  const dueLabel = (r) => {
    if (r.due_hours != null) return `@ ${r.due_hours} hrs`;
    if (r.due_cycles != null) return `@ ${r.due_cycles} landings`;
    if (r.due_date) return `due ${r.due_date}`;
    if (r.due_display) return r.due_display;
    return "—";
  };

  return (
    <Dialog open={!!scan} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="plan-scan-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Upload className="w-4 h-4" /> Scanned — {scan?.filename}</DialogTitle>
          <DialogDescription>
            {rows.length} job{rows.length === 1 ? "" : "s"} found
            {dupCount > 0 && <> · <span className="text-amber-700 font-medium" data-testid="plan-scan-dup-count">{dupCount} already on {tail}&apos;s plan (unchecked)</span></>}
            {" "}· pick the ones to transcribe onto {tail}&apos;s plan
          </DialogDescription>
          <p className="text-[10px] text-gray-400" data-testid="plan-scan-privacy">
            Scanned in memory only — the file itself is never stored on the server.
          </p>
        </DialogHeader>
        {rows.length === 0 ? (
          <p className="text-sm text-gray-400 py-6 text-center" data-testid="plan-scan-empty">No usable rows found — check the file has descriptions in column D.</p>
        ) : (
          <>
            <div
              className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-gray-500 border-b border-summit-hairline pb-2 cursor-pointer select-none"
              onClick={() => {
                const v = !allOn;
                const next = { ...checked };
                newRows.forEach((r) => { next[r.row] = v; });
                setChecked(next);
              }}
              data-testid="plan-scan-all-row"
            >
              <Checkbox checked={allOn} onClick={(e) => e.stopPropagation()} onCheckedChange={(v) => {
                const next = { ...checked };
                newRows.forEach((r) => { next[r.row] = !!v; });
                setChecked(next);
              }} data-testid="plan-scan-all" />
              Select all new ({selected.length}/{rows.length})
            </div>
            <div className="max-h-[24rem] overflow-y-auto space-y-1.5 pr-1" data-testid="plan-scan-rows">
              {rows.map((r) => (
                <div key={r.row} className={`flex items-start gap-2.5 border rounded-md px-2.5 py-2 cursor-pointer select-none transition-colors ${checked[r.row] ? "border-summit-primary bg-summit-glacier/40" : "border-summit-hairline hover:bg-summit-glacier/20"} ${r.already_planned && !checked[r.row] ? "opacity-55" : ""}`} onClick={() => setChecked((m) => ({ ...m, [r.row]: !m[r.row] }))} data-testid={`plan-scan-row-${r.row}`}>
                  <Checkbox className="mt-0.5" checked={!!checked[r.row]} onClick={(e) => e.stopPropagation()} onCheckedChange={(v) => setChecked((m) => ({ ...m, [r.row]: !!v }))} data-testid={`plan-scan-check-${r.row}`} />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2 flex-wrap">
                      {r.already_planned && (
                        <span className={`px-1.5 py-0.5 rounded-full font-mono text-[9px] uppercase tracking-widest shrink-0 ${r.already_status === "maintenance" ? "bg-gray-200 text-gray-600" : "bg-amber-100 text-amber-800"}`} data-testid={`plan-scan-dup-${r.row}`}>
                          {r.already_status === "maintenance" ? "In maintenance" : "Already planned"}
                        </span>
                      )}
                      {r.limiter && <span className="px-1.5 py-0.5 rounded-full font-mono text-[9px] uppercase tracking-widest bg-sky-100 text-sky-800 shrink-0">{r.limiter}</span>}
                      {r.merged_rows > 1 && <span className="px-1.5 py-0.5 rounded-full font-mono text-[9px] uppercase tracking-widest bg-violet-100 text-violet-700 shrink-0" data-testid={`plan-scan-merged-${r.row}`}>{r.merged_rows} rows merged</span>}
                      {r.remaining_hours != null && <span className="px-1.5 py-0.5 rounded-full font-mono text-[9px] bg-emerald-50 text-emerald-800 border border-emerald-200 shrink-0" data-testid={`plan-scan-br-hrs-${r.row}`}>BR {r.remaining_hours} hrs</span>}
                      {r.remaining_cycles != null && <span className="px-1.5 py-0.5 rounded-full font-mono text-[9px] bg-emerald-50 text-emerald-800 border border-emerald-200 shrink-0" data-testid={`plan-scan-br-cyc-${r.row}`}>BR {r.remaining_cycles} cyc</span>}
                      {r.remaining_days != null && <span className="px-1.5 py-0.5 rounded-full font-mono text-[9px] bg-emerald-50 text-emerald-800 border border-emerald-200 shrink-0" data-testid={`plan-scan-br-days-${r.row}`}>BR {r.remaining_days} days</span>}
                      <span className={`font-mono text-[10px] ${isOverdue(r.due_date) ? "text-rose-600 font-bold" : "text-gray-500"}`}>{dueLabel(r)}</span>
                    </span>
                    <span className="block text-xs mt-0.5 whitespace-pre-wrap">{r.description}</span>
                    {r.jobcard_info && <span className="block text-[10px] text-sky-700 mt-0.5 font-mono">Jobcard: {r.jobcard_info}</span>}
                    {r.part_details && <span className="block text-[10px] text-gray-500 mt-0.5">Parts: {r.part_details}</span>}
                  </span>
                </div>
              ))}
            </div>
            <Button className="w-full bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={importSelected} disabled={busy || selected.length === 0} data-testid="plan-scan-import">
              Add {selected.length} selected to {tail}&apos;s plan
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ToleranceDialog({ job, onClose, onApplied }) {
  const [vals, setVals] = useState({ hours: "", cycles: "", days: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (job) setVals({ hours: "", cycles: "", days: "" }); }, [job]);

  const apply = async () => {
    const payload = {
      hours: vals.hours === "" ? null : Number(vals.hours),
      cycles: vals.cycles === "" ? null : Number(vals.cycles),
      days: vals.days === "" ? null : Number(vals.days),
    };
    if (!payload.hours && !payload.cycles && !payload.days) { toast.error("Enter at least one tolerance value"); return; }
    setBusy(true);
    try {
      await api.post(`/planning/jobs/${job.id}/tolerance`, payload);
      toast.success("Tolerance applied — due fields extended");
      onClose();
      onApplied();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to apply tolerance");
    } finally {
      setBusy(false);
    }
  };

  const field = (key, label, step) => (
    <div>
      <Label className="text-xs uppercase tracking-widest text-gray-500">{label}</Label>
      <Input className="mt-1" type="number" step={step} placeholder="0" value={vals[key]} onChange={(e) => setVals((s) => ({ ...s, [key]: e.target.value }))} data-testid={`plan-tol-${key}`} />
    </div>
  );

  return (
    <Dialog open={!!job} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="plan-tol-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Timer className="w-4 h-4" /> Apply tolerance</DialogTitle>
          <DialogDescription className="whitespace-pre-wrap">{job?.tail} — {job?.description?.slice(0, 120)}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          {field("hours", "Hours", "0.1")}
          {field("cycles", "Cycles", "1")}
          {field("days", "Days", "1")}
        </div>
        <p className="text-[10px] text-gray-500" data-testid="plan-tol-hint">
          Each value is added to the job&apos;s current dues — hours → due at hours{job?.due_hours != null ? ` (${job.due_hours})` : ""}, cycles → due at landings{job?.due_cycles != null ? ` (${job.due_cycles})` : ""}, days → next due date{job?.due_date ? ` (${job.due_date})` : ""}. Toggle off to revert.
        </p>
        <Button className="w-full bg-amber-600 hover:bg-amber-700 text-white" onClick={apply} disabled={busy} data-testid="plan-tol-apply">
          Apply tolerance
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function PlanningJobCard({ job, ac, user, onPush, onEdit, onDeleted, onTolerance, onUrgency }) {
  const pushed = job.status === "pushed";
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [commentBusy, setCommentBusy] = useState(false);
  const woComments = job.wo_comments || [];

  const addComment = async () => {
    const t = newComment.trim();
    if (!t) { toast.error("Write a comment first"); return; }
    setCommentBusy(true);
    try {
      await api.post(`/maintenance/work-orders/${job.maint_wo_id}/comments`, { text: t });
      setNewComment("");
      toast.success("Comment added to the jobcard");
      onDeleted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add comment");
    } finally {
      setCommentBusy(false);
    }
  };
  const hrsLeft = job.due_hours != null && ac?.airframe_hours != null ? +(job.due_hours - ac.airframe_hours).toFixed(1) : null;
  const cycLeft = job.due_cycles != null && ac?.cycles != null ? job.due_cycles - ac.cycles : null;
  const daysLeft = job.due_date ? Math.round((new Date(job.due_date + "T00:00:00") - new Date(todayIso() + "T00:00:00")) / 86400000) : null;
  const overdue = (daysLeft != null && daysLeft < 0) || (hrsLeft != null && hrsLeft < 0) || (cycLeft != null && cycLeft < 0);
  const hasJobcard = !!(job.jobcard_info || "").trim();
  const leftCls = (v) => (v <= 0 ? "text-rose-600 font-bold" : v <= 25 ? "text-amber-600 font-bold" : "text-gray-500");
  const remove = async () => {
    if (!window.confirm(`Delete planned job "${job.description.slice(0, 60)}"?`)) return;
    try {
      await api.delete(`/planning/jobs/${job.id}`);
      toast.success("Planned job deleted");
      onDeleted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };
  const pullBack = async () => {
    if (!window.confirm(`Pull "${job.description.slice(0, 60)}" back to Planning?\nThe open Maintenance jobcard will be removed.`)) return;
    try {
      await api.post(`/planning/jobs/${job.id}/pull-back`);
      toast.success("Job pulled back to Planning — you can edit it again");
      onDeleted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to pull back");
    }
  };
  return (
    <div className={`border rounded-md px-3 py-2.5 ${!pushed && overdue ? "border-rose-400 border-l-4 bg-rose-50/70" : pushed ? "border-emerald-300 bg-white" : "border-summit-hairline bg-white"}`} data-testid={`plan-job-${job.id}`}>
      <div className="flex items-start gap-2 flex-wrap">
        {!pushed && overdue && (
          <span className="px-1.5 py-0.5 rounded-full font-mono text-[9px] uppercase tracking-widest bg-rose-600 text-white shrink-0 animate-pulse" data-testid={`plan-job-overdue-badge-${job.id}`}>Overdue</span>
        )}
        {pushed ? (
          <span className="px-1.5 py-0.5 rounded-full font-mono text-[9px] uppercase tracking-widest bg-emerald-600 text-white shrink-0" data-testid={`plan-job-pushed-${job.id}`}>Pushed</span>
        ) : (
          <span className="px-1.5 py-0.5 rounded-full font-mono text-[9px] uppercase tracking-widest bg-sky-100 text-sky-800 shrink-0" data-testid={`plan-job-planned-${job.id}`}>Planned</span>
        )}
        {job.limiter && <span className="px-1.5 py-0.5 rounded-full font-mono text-[9px] uppercase tracking-widest bg-gray-100 text-gray-600 shrink-0" data-testid={`plan-job-limiter-${job.id}`}>{job.limiter}</span>}
        {job.source === "upload" && <span className="px-1.5 py-0.5 rounded-full font-mono text-[9px] uppercase tracking-widest bg-violet-100 text-violet-700 shrink-0">Scanned</span>}
        {job.tol_applied && (
          <span className="px-1.5 py-0.5 rounded-full font-mono text-[9px] uppercase tracking-widest bg-amber-100 text-amber-800 border border-amber-300 shrink-0" title={`Tolerance applied by ${job.tol_by_name || "unknown"}${job.tol_at ? ` · ${fmtWhen(job.tol_at)}` : ""}`} data-testid={`plan-job-tol-badge-${job.id}`}>
            On tolerance
          </span>
        )}
        <span className="ml-auto flex items-center gap-1 shrink-0">
          {!pushed && (
            <button className="p-0.5 rounded text-gray-300 hover:text-summit-primary" onClick={() => onEdit(job)} title="Edit" data-testid={`plan-job-edit-${job.id}`}>
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
          {(user.role === "admin" || job.created_by === user.id) && (
            <button className="p-0.5 rounded text-gray-300 hover:text-rose-600" onClick={remove} title="Delete" data-testid={`plan-job-delete-${job.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </span>
      </div>
      <div className="text-xs font-medium leading-snug whitespace-pre-wrap mt-1.5">{job.description}</div>
      {job.jobcard_info && <div className="text-[10px] text-sky-700 mt-1 font-mono" data-testid={`plan-job-jobcard-${job.id}`}>Jobcard: {job.jobcard_info}</div>}
      {job.part_details && <div className="text-[10px] text-gray-600 mt-1" data-testid={`plan-job-parts-${job.id}`}>Parts: {job.part_details}</div>}
      {(job.attachments || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`plan-job-attachments-${job.id}`}>
          {job.attachments.map((a) => (
            <a key={a.id} href={`${API_BASE}/planning/files/${a.id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[10px] text-summit-primary hover:underline border border-summit-hairline rounded-full px-2 py-0.5 bg-white max-w-[12rem]" data-testid={`plan-job-attach-${a.id}`}>
              <Paperclip className="w-3 h-3 shrink-0" /> <span className="truncate">{a.filename}</span>
            </a>
          ))}
        </div>
      )}
      {job.updated_at && (
        <div className={`text-[10px] mt-1 font-mono ${job.updated_by_id === user.id ? "text-emerald-700 font-bold" : "text-gray-500"}`} data-testid={`plan-job-updated-${job.id}`}>
          {job.updated_by_id === user.id ? "You updated" : `Updated by ${job.updated_by_name}`} · {fmtWhen(job.updated_at)}
        </div>
      )}
      <div className="text-[10px] text-gray-500 mt-1">
        {job.due_date && (
          <span className={`font-mono ${isOverdue(job.due_date) ? "text-rose-600 font-bold" : ""}`}>
            due {job.due_date}
            {daysLeft != null && (
              <span className={leftCls(daysLeft)} data-testid={`plan-job-days-left-${job.id}`}>
                {" "}({daysLeft <= 0 ? `${Math.abs(daysLeft)} days over` : `${daysLeft} days left`})
              </span>
            )}
            {" · "}
          </span>
        )}
        {job.due_hours != null && (
          <span className="font-mono">
            @{job.due_hours} hrs
            {hrsLeft != null && (
              <span className={leftCls(hrsLeft)} data-testid={`plan-job-hrs-left-${job.id}`}>
                {" "}({hrsLeft <= 0 ? `${Math.abs(hrsLeft)} hrs over` : `${hrsLeft} hrs left`})
              </span>
            )}
            {" · "}
          </span>
        )}
        {job.due_cycles != null && (
          <span className="font-mono">
            @{job.due_cycles} landings
            {cycLeft != null && (
              <span className={leftCls(cycLeft)} data-testid={`plan-job-cyc-left-${job.id}`}>
                {" "}({cycLeft <= 0 ? `${Math.abs(cycLeft)} over` : `${cycLeft} left`})
              </span>
            )}
            {" · "}
          </span>
        )}
        {job.created_by_name} · {fmtWhen(job.created_at)}
      </div>
      {pushed ? (
        <>
          <div className="mt-1.5 flex items-center gap-3 flex-wrap text-[11px]">
            <span className="text-gray-500">Pushed by {job.pushed_by_name} · {fmtWhen(job.pushed_at)}</span>
            <Link to={`/fleet/maintenance?tail=${encodeURIComponent(job.tail)}`} className="inline-flex items-center gap-1 text-summit-primary hover:underline" data-testid={`plan-job-view-maint-${job.id}`}>
              <ExternalLink className="w-3 h-3" /> View in Maintenance
            </Link>
            <button className="inline-flex items-center gap-1 text-sky-700 hover:underline" onClick={pullBack} data-testid={`plan-job-pullback-${job.id}`}>
              <Undo2 className="w-3 h-3" /> Pull back to Planning
            </button>
            {job.maint_wo_id && (
              <button className="inline-flex items-center gap-1 text-gray-500 hover:text-summit-primary" onClick={() => setShowComments((v) => !v)} title="Comments on the maintenance jobcard" data-testid={`plan-job-comments-toggle-${job.id}`}>
                <MessageSquare className="w-3 h-3" /> Comments{woComments.length > 0 && <span className="font-mono text-[10px]">({woComments.length})</span>}
              </button>
            )}
            {job.maint_wo_id && (
              <button
                className={`ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full border font-mono text-[9px] uppercase tracking-widest transition-colors ${(job.pushed_priority || "Medium") === "Urgent" ? "bg-rose-600 border-rose-600 text-white" : "border-summit-hairline text-gray-500 hover:border-rose-400 hover:text-rose-600"}`}
                onClick={() => onUrgency(job, (job.pushed_priority || "Medium") === "Urgent" ? "Medium" : "Urgent")}
                title={(job.pushed_priority || "Medium") === "Urgent" ? "Marked urgent — click to set back to Medium" : "Mark the pushed jobcard as Urgent"}
                data-testid={`plan-job-urgent-${job.id}`}
              >
                <AlertTriangle className="w-3 h-3" /> {(job.pushed_priority || "Medium") === "Urgent" ? "Urgent" : "Mark urgent"}
              </button>
            )}
          </div>
          {showComments && job.maint_wo_id && (
            <div className="mt-2 border-t border-summit-hairline pt-2 space-y-1.5" data-testid={`plan-job-comments-${job.id}`}>
              {woComments.length === 0 && <p className="text-[10px] text-gray-400">No comments yet.</p>}
              {woComments.map((c) => (
                <div key={c.id} className="text-[11px] bg-summit-canvas/50 rounded px-2 py-1" data-testid={`plan-job-comment-${c.id}`}>
                  <span className="font-medium">{c.by_name}</span>
                  <span className="text-gray-400 font-mono text-[9px]"> · {fmtWhen(c.created_at)}</span>
                  <div className="whitespace-pre-wrap">{c.text}</div>
                </div>
              ))}
              <div className="flex items-center gap-1.5">
                <input
                  className="flex-1 h-8 px-2 rounded-md border border-summit-hairline bg-white text-[11px]"
                  placeholder="Add a comment — shows on the jobcard too…"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") addComment(); }}
                  maxLength={2000}
                  data-testid={`plan-job-comment-input-${job.id}`}
                />
                <Button size="sm" className="h-8 text-[11px]" onClick={addComment} disabled={commentBusy} data-testid={`plan-job-comment-add-${job.id}`}>
                  Add
                </Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="mt-1.5 flex items-center gap-3 flex-wrap">
          <button
            className={`inline-flex items-center gap-1 text-[11px] ${hasJobcard ? "text-emerald-700 hover:underline" : "text-gray-400 cursor-not-allowed"}`}
            title={hasJobcard ? "Raise this job as an open jobcard on the Maintenance page" : "Jobcard information is required before pushing to Maintenance"}
            onClick={() => {
              if (!hasJobcard) { toast.error("Jobcard information is required before pushing — add it to the job first"); return; }
              onPush(job);
            }}
            data-testid={`plan-job-push-${job.id}`}
          >
            <Send className="w-3 h-3" /> Push to Maintenance
          </button>
          <span className="inline-flex items-center gap-1.5" title={job.tol_applied ? `Tolerance applied by ${job.tol_by_name || "unknown"}${job.tol_at ? ` · ${fmtWhen(job.tol_at)}` : ""}` : "Apply tolerance to this job's dues"}>
            <Switch className="scale-[0.65] -mx-1" checked={!!job.tol_applied} onCheckedChange={(v) => onTolerance(job, v)} data-testid={`plan-job-tol-toggle-${job.id}`} />
            <span className="font-mono text-[9px] uppercase tracking-widest text-gray-500">Tolerance</span>
            {job.tol_applied && (
              <span className="px-1.5 py-0.5 rounded-full font-mono text-[9px] bg-amber-100 text-amber-800 border border-amber-300" data-testid={`plan-job-tol-chip-${job.id}`}>
                {[job.tol_hours ? `+${job.tol_hours}h` : null, job.tol_cycles ? `+${job.tol_cycles}c` : null, job.tol_days ? `+${job.tol_days}d` : null].filter(Boolean).join(" · ")}
              </span>
            )}
          </span>
        </div>
      )}
    </div>
  );
}

function AcInfoStrip({ ac, onSaved }) {
  const [vals, setVals] = useState({ log_number: "", airframe_hours: "", cycles: "" });
  useEffect(() => {
    setVals({ log_number: ac?.log_number ?? "", airframe_hours: ac?.airframe_hours ?? "", cycles: ac?.cycles ?? "" });
  }, [ac?.tail, ac?.log_number, ac?.airframe_hours, ac?.cycles]);
  if (!ac) return null;
  const commit = async (key) => {
    const orig = ac[key] ?? "";
    const v = vals[key];
    if (String(v) === String(orig)) return;
    const payload = key === "log_number" ? { log_number: v || null } : { [key]: v === "" ? null : Number(v) };
    try {
      await api.patch(`/maintenance/aircraft/${ac.tail}/details`, payload);
      toast.success(`${ac.tail} info updated`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
      setVals((s) => ({ ...s, [key]: orig }));
    }
  };
  const field = (key, label, type, w) => (
    <label className="flex items-center gap-1.5">
      <span className="font-mono text-[9px] uppercase tracking-widest text-gray-500">{label}</span>
      <input
        type={type}
        step={type === "number" ? "any" : undefined}
        min={type === "number" ? "0" : undefined}
        className={`h-7 ${w} px-1.5 text-xs font-mono text-center bg-white border border-summit-hairline focus:border-summit-primary rounded outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none`}
        value={vals[key] ?? ""}
        onChange={(e) => setVals((s) => ({ ...s, [key]: e.target.value }))}
        onBlur={() => commit(key)}
        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
        data-testid={`plan-ac-${key}`}
      />
    </label>
  );
  return (
    <div className="mt-2 inline-flex items-center gap-3 flex-wrap border border-summit-hairline rounded-md bg-white px-3 py-1.5" data-testid="plan-ac-info">
      {field("log_number", "Log #", "text", "w-[90px]")}
      {field("airframe_hours", "Hours", "number", "w-[90px]")}
      {field("cycles", "Cycles", "number", "w-[80px]")}
      {ac.details_updated_at && (
        <span className="font-mono text-[9px] text-gray-400" data-testid="plan-ac-info-upd">upd {fmtWhen(ac.details_updated_at)} · {ac.details_updated_by_name}</span>
      )}
    </div>
  );
}

export default function Planning() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [orders, setOrders] = useState([]);
  const [selectedTail, setSelectedTail] = useState("all");
  const [query, setQuery] = useState("");
  const [jobQuery, setJobQuery] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editJob, setEditJob] = useState(null);
  const pushBusyRef = useRef(false);
  const directPush = async (job) => {
    if (pushBusyRef.current) return;
    pushBusyRef.current = true;
    try {
      await api.post(`/planning/jobs/${job.id}/push`, { priority: "Medium", comment: null });
      toast.success(`Pushed to Maintenance — open job created on ${job.tail}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to push job");
    } finally {
      pushBusyRef.current = false;
    }
  };

  const setUrgency = async (job, priority) => {
    try {
      await api.patch(`/planning/jobs/${job.id}/urgency`, { priority });
      toast.success(priority === "Urgent" ? "Jobcard marked Urgent" : "Urgency set back to Medium");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to change urgency");
    }
  };
  const [tolJob, setTolJob] = useState(null);
  const [scan, setScan] = useState(null);
  const [scanning, setScanning] = useState(false);
  const fileRef = useRef(null);
  const hasAccess = user && (user.planning_access || user.role === "admin");
  const [favs, setFavs] = useState([]);

  useEffect(() => { if (user) setFavs(user.favourite_aircraft || []); }, [user]);

  const toggleFav = async (tail, e) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      const r = favs.includes(tail)
        ? await api.delete(`/users/me/favourites/${tail}`)
        : await api.post(`/users/me/favourites/${tail}`);
      setFavs(r.data.favourite_aircraft || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update favourites");
    }
  };

  const load = async () => {
    try {
      const [a, j, o] = await Promise.all([
        api.get("/maintenance/aircraft"),
        api.get("/planning/jobs"),
        api.get("/maintenance/work-orders"),
      ]);
      setItems(a.data || []);
      setJobs(j.data || []);
      setOrders(o.data || []);
    } catch (e) {
      setItems([]);
      toast.error(e?.response?.data?.detail || "Failed to load planning data");
    }
  };
  useEffect(() => { if (hasAccess) load(); }, [hasAccess]); // eslint-disable-line react-hooks/exhaustive-deps

  const sideList = useMemo(() => {
    let list = items || [];
    const q = query.trim().toLowerCase();
    if (q) list = list.filter((i) => [i.tail, i.fleet].some((v) => (v || "").toLowerCase().includes(q)));
    const rank = (i) => (i.status === "offline" ? 2 : favs.includes(i.tail) ? 0 : 1);
    return [...list].sort((a, b) => rank(a) - rank(b));
  }, [items, query, favs]);

  const acByTail = useMemo(() => Object.fromEntries((items || []).map((i) => [i.tail, i])), [items]);

  const shownJobs = useMemo(() => {
    let list = selectedTail === "all" ? jobs : jobs.filter((j) => j.tail === selectedTail);
    const q = jobQuery.trim().toLowerCase();
    if (q) list = list.filter((j) => [j.description, j.part_details, j.jobcard_info, j.limiter, j.tail].some((v) => (v || "").toLowerCase().includes(q)));
    return list;
  }, [jobs, selectedTail, jobQuery]);

  const urgency = (j) => {
    const ac = acByTail[j.tail];
    const vals = [];
    if (j.due_date) vals.push((new Date(j.due_date + "T00:00:00") - new Date(todayIso() + "T00:00:00")) / 86400000);
    if (j.due_hours != null && ac?.airframe_hours != null) vals.push((j.due_hours - ac.airframe_hours) / 5);
    if (j.due_cycles != null && ac?.cycles != null) vals.push((j.due_cycles - ac.cycles) / 4);
    return vals.length ? Math.min(...vals) : Infinity;
  };
  const plannedJobs = useMemo(
    () => shownJobs.filter((j) => j.status === "planned").map((j) => [urgency(j), j]).sort((a, b) => a[0] - b[0]).map(([, j]) => j),
    [shownJobs, acByTail] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const pushedJobs = useMemo(
    () => shownJobs.filter((j) => j.status === "pushed").map((j) => [urgency(j), j]).sort((a, b) => a[0] - b[0]).map(([, j]) => j),
    [shownJobs, acByTail] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const plannedCountByTail = useMemo(() => {
    const m = {};
    jobs.forEach((j) => { if (j.status === "planned") m[j.tail] = (m[j.tail] || 0) + 1; });
    return m;
  }, [jobs]);

  const overdueCountByTail = useMemo(() => {
    const m = {};
    jobs.forEach((j) => {
      if (j.status !== "planned") return;
      const ac = acByTail[j.tail];
      const over = (j.due_date && j.due_date < todayIso()) ||
        (j.due_hours != null && ac?.airframe_hours != null && j.due_hours < ac.airframe_hours) ||
        (j.due_cycles != null && ac?.cycles != null && j.due_cycles < ac.cycles);
      if (over) m[j.tail] = (m[j.tail] || 0) + 1;
    });
    return m;
  }, [jobs, acByTail]);

  const onTolerance = async (job, on) => {
    if (on) { setTolJob(job); return; }
    if (!window.confirm("Remove tolerance? The due fields revert to their original values.")) return;
    try {
      await api.delete(`/planning/jobs/${job.id}/tolerance`);
      toast.success("Tolerance removed — dues reverted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove tolerance");
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (selectedTail && selectedTail !== "all") fd.append("tail", selectedTail);
      const { data } = await api.post("/planning/scan", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setScan(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to scan file");
    } finally {
      setScanning(false);
    }
  };

  if (!user) return null;
  if (!hasAccess) {
    return (
      <div className="max-w-lg mx-auto px-4 py-20 text-center" data-testid="planning-no-access">
        <Lock className="w-8 h-8 mx-auto text-gray-300" />
        <h1 className="font-display text-xl font-semibold mt-3">Fleet Planning</h1>
        <p className="text-sm text-gray-500 mt-1">You don&apos;t have access to this page. Ask an admin to grant Planning access in Admin Settings.</p>
      </div>
    );
  }

  const selectedAc = selectedTail === "all" ? null : (items || []).find((i) => i.tail === selectedTail);
  const openMaintJobs = selectedAc ? orders.filter((o) => o.tail === selectedTail && o.status === "open").length : 0;

  return (
    <div className="flex" style={{ minHeight: "calc(100vh - 56px)" }}>
      <aside className={`hidden md:flex flex-col shrink-0 bg-white border-r border-summit-hairline ${sidebarOpen ? "w-44 lg:w-52 xl:w-60" : "w-10"}`} data-testid="plan-sidebar">
        {!sidebarOpen ? (
          <button className="p-2.5 flex justify-center hover:bg-summit-glacier/40" onClick={() => setSidebarOpen(true)} title="Show aircraft list" data-testid="plan-sidebar-expand">
            <PanelLeftOpen className="w-4 h-4 text-gray-500" />
          </button>
        ) : (
        <>
        <div className="p-3 border-b border-summit-hairline">
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2 flex items-center justify-between">
            <span>Aircraft</span>
            <button className="p-0.5 rounded hover:bg-summit-glacier/60 text-gray-400 hover:text-gray-600" onClick={() => setSidebarOpen(false)} title="Hide aircraft list" data-testid="plan-sidebar-collapse">
              <PanelLeftClose className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input className="h-8 pl-8 text-xs" placeholder="Search…" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="plan-search" />
            {query && <button className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" onClick={() => setQuery("")} data-testid="plan-search-clear"><X className="w-3.5 h-3.5" /></button>}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto py-2">
          <button
            className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 border-l-2 ${selectedTail === "all" ? "border-summit-primary bg-summit-glacier/60 font-medium" : "border-transparent hover:bg-summit-glacier/40"}`}
            onClick={() => setSelectedTail("all")}
            data-testid="plan-side-all"
          >
            <LayoutList className="w-3.5 h-3.5 text-gray-400" /> All Aircraft
            <span className="ml-auto font-mono text-[10px] text-gray-400">{jobs.filter((j) => j.status === "planned").length}</span>
          </button>
          {sideList.map((i) => (
            <button
              key={i.tail}
              className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 border-l-2 ${selectedTail === i.tail ? "border-summit-primary bg-summit-glacier/60 font-medium" : "border-transparent hover:bg-summit-glacier/40"}`}
              onClick={() => setSelectedTail(i.tail)}
              data-testid={`plan-side-item-${i.tail}`}
            >
              <Plane className={`w-3.5 h-3.5 shrink-0 ${(AC_STATUS[i.status] || AC_STATUS.normal).plane}`} fill="currentColor" />
              <span className="truncate">{i.tail}</span>
              <span className="ml-auto flex items-center gap-1 shrink-0">
                {overdueCountByTail[i.tail] > 0 && (
                  <span
                    className="min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white font-mono text-[10px] font-bold inline-flex items-center justify-center shrink-0 animate-pulse"
                    title={`${overdueCountByTail[i.tail]} overdue job${overdueCountByTail[i.tail] === 1 ? "" : "s"}`}
                    data-testid={`plan-side-overdue-${i.tail}`}
                  >
                    {overdueCountByTail[i.tail]}
                  </span>
                )}
                {plannedCountByTail[i.tail] > 0 && (
                  <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-sky-100 text-sky-800 font-mono text-[10px] inline-flex items-center justify-center shrink-0" data-testid={`plan-side-count-${i.tail}`}>
                    {plannedCountByTail[i.tail]}
                  </span>
                )}
                <span
                  role="button"
                  tabIndex={-1}
                  onClick={(e) => toggleFav(i.tail, e)}
                  title={favs.includes(i.tail) ? "Remove favourite" : "Pin as favourite"}
                  data-testid={`plan-fav-${i.tail}`}
                >
                  <Star className={`w-3.5 h-3.5 transition-colors ${favs.includes(i.tail) ? "text-amber-400 fill-amber-400" : "text-gray-300 hover:text-amber-400"}`} />
                </span>
              </span>
            </button>
          ))}
          {items !== null && sideList.length === 0 && <p className="px-3 py-2 text-xs text-gray-400">No matches.</p>}
        </div>
        </>
        )}
      </aside>

      <main className="flex-1 min-w-0 px-4 sm:px-6 py-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-display text-3xl font-semibold tracking-tight" data-testid="plan-heading">Fleet Planning</h1>
            <div className="mt-1 font-mono text-[11px] text-gray-500" data-testid="plan-subtitle">
              {selectedTail === "all"
                ? `${jobs.filter((j) => j.status === "planned").length} planned · ${jobs.filter((j) => j.status === "pushed").length} pushed`
                : (
                  <span className="inline-flex items-center gap-2 flex-wrap">
                    {selectedTail} — {(AC_STATUS[selectedAc?.status] || AC_STATUS.normal).label}
                    <Link to={`/fleet/maintenance?tail=${encodeURIComponent(selectedTail)}`} className="inline-flex items-center gap-1 text-summit-primary hover:underline" data-testid="plan-open-maint-link">
                      <ClipboardList className="w-3 h-3" /> {openMaintJobs} open in Maintenance <ExternalLink className="w-3 h-3" />
                    </Link>
                  </span>
                )}
            </div>
            {selectedTail !== "all" && selectedAc && <AcInfoStrip ac={selectedAc} onSaved={load} />}
          </div>
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept=".xlsx,.xlsm" className="hidden" onChange={onFile} data-testid="plan-upload-input" />
            <Button
              variant="outline"
              onClick={() => {
                if (selectedTail === "all") { toast.error("Select an aircraft first — scanned jobs land on its plan"); return; }
                fileRef.current?.click();
              }}
              disabled={scanning}
              data-testid="plan-upload-btn"
            >
              <Upload className="w-4 h-4 mr-1.5" /> {scanning ? "Scanning…" : "Upload / scan xlsx"}
            </Button>
            <Button
              className="bg-summit-primary hover:bg-summit-primary-soft text-white"
              onClick={() => {
                if (selectedTail === "all") { toast.error("Select an aircraft first"); return; }
                setEditJob(null);
                setShowForm(true);
              }}
              data-testid="plan-add-btn"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Add job
            </Button>
          </div>
        </div>

        <select
          className="md:hidden mt-3 h-9 w-full px-2 rounded-md border border-summit-hairline bg-white text-sm"
          value={selectedTail}
          onChange={(e) => setSelectedTail(e.target.value)}
          data-testid="plan-mobile-select"
        >
          <option value="all">All Aircraft</option>
          {sideList.map((i) => <option key={i.tail} value={i.tail}>{i.tail}</option>)}
        </select>

        {selectedTail === "all" ? (
          <div className="mt-4" data-testid="plan-summary">
            {items === null ? (
              <div className="text-center text-gray-400 text-sm py-12">Loading…</div>
            ) : (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-2.5">
                {sideList.map((i) => {
                  const tailJobs = jobs.filter((j) => j.tail === i.tail);
                  const planned = tailJobs.filter((j) => j.status === "planned").length;
                  const pushed = tailJobs.filter((j) => j.status === "pushed").length;
                  let last = null;
                  tailJobs.forEach((j) => {
                    [["added", j.created_at, j.created_by_name],
                     ["updated", j.updated_at, j.updated_by_name],
                     ["pushed", j.pushed_at, j.pushed_by_name]].forEach(([verb, t, n]) => {
                      if (t && (!last || t > last.t)) last = { verb, t, n };
                    });
                  });
                  const st = AC_STATUS[i.status] || AC_STATUS.normal;
                  return (
                    <button
                      key={i.tail}
                      className="text-left border border-summit-hairline rounded-md bg-white px-3 py-2.5 hover:border-summit-primary hover:shadow-sm transition-all"
                      onClick={() => setSelectedTail(i.tail)}
                      data-testid={`plan-summary-row-${i.tail}`}
                    >
                      <div className="flex items-center gap-2">
                        <Plane className={`w-3.5 h-3.5 shrink-0 ${st.plane}`} fill="currentColor" />
                        <span className="font-display font-semibold">{i.tail}</span>
                        <span className="font-mono text-[9px] uppercase tracking-widest text-gray-400">{st.label}</span>
                        {favs.includes(i.tail) && <Star className="w-3 h-3 text-amber-400 fill-amber-400 shrink-0" />}
                        <span className="ml-auto font-mono text-[10px] text-gray-500 shrink-0">
                          <span className={planned > 0 ? "text-sky-700 font-bold" : ""}>{planned} planned</span>
                          {" · "}
                          <span className={pushed > 0 ? "text-emerald-700 font-bold" : ""}>{pushed} pushed</span>
                        </span>
                      </div>
                      <div className="mt-1 font-mono text-[10px] text-gray-400" data-testid={`plan-summary-last-${i.tail}`}>
                        {last ? `Last ${last.verb} by ${last.n} · ${fmtWhen(last.t)}` : "No planning activity yet"}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
        <>
        <div className="mt-4 relative max-w-md" data-testid="plan-job-search-wrap">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input className="h-9 pl-8 text-sm" placeholder="Search jobs — description, parts, jobcard…" value={jobQuery} onChange={(e) => setJobQuery(e.target.value)} data-testid="plan-job-search" />
          {jobQuery && <button className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" onClick={() => setJobQuery("")} data-testid="plan-job-search-clear"><X className="w-3.5 h-3.5" /></button>}
        </div>
        <div className="mt-3 grid lg:grid-cols-2 gap-4 items-start" data-testid="plan-columns">
          <section data-testid="plan-col-planned">
            <h2 className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2 flex items-center gap-1.5">
              <CalendarClock className="w-3.5 h-3.5" /> Planned · {plannedJobs.length}
            </h2>
            <div className="space-y-2">
              {items === null ? (
                <div className="text-center text-gray-400 text-sm py-12">Loading…</div>
              ) : plannedJobs.length === 0 ? (
                <div className="text-center text-gray-400 text-sm py-10 border border-dashed border-summit-hairline rounded-lg" data-testid="plan-empty">
                  {selectedTail === "all" ? "No planned jobs yet — pick an aircraft and add or upload jobs." : `No planned jobs for ${selectedTail} yet.`}
                </div>
              ) : (
                plannedJobs.map((j) => (
                  <div key={j.id}>
                    {selectedTail === "all" && (
                      <div className="font-mono text-[9px] uppercase tracking-widest text-gray-400 mb-0.5">{j.tail} · {j.fleet || ""}</div>
                    )}
                    <PlanningJobCard
                      job={j}
                      ac={acByTail[j.tail]}
                      user={user}
                      onPush={directPush}
                      onUrgency={setUrgency}
                      onEdit={(job) => { setEditJob(job); setShowForm(true); }}
                      onDeleted={load}
                      onTolerance={onTolerance}
                    />
                  </div>
                ))
              )}
            </div>
          </section>
          <section data-testid="plan-col-pushed">
            <h2 className="font-mono text-[10px] uppercase tracking-widest text-emerald-700 mb-2 flex items-center gap-1.5">
              <Send className="w-3.5 h-3.5" /> Pushed to Maintenance · {pushedJobs.length}
            </h2>
            <div className="space-y-2">
              {pushedJobs.length === 0 ? (
                <div className="text-center text-gray-400 text-sm py-10 border border-dashed border-summit-hairline rounded-lg" data-testid="plan-pushed-empty">
                  Nothing pushed yet.
                </div>
              ) : (
                pushedJobs.map((j) => (
                  <div key={j.id}>
                    {selectedTail === "all" && (
                      <div className="font-mono text-[9px] uppercase tracking-widest text-gray-400 mb-0.5">{j.tail} · {j.fleet || ""}</div>
                    )}
                    <PlanningJobCard
                      job={j}
                      ac={acByTail[j.tail]}
                      user={user}
                      onPush={directPush}
                      onUrgency={setUrgency}
                      onEdit={(job) => { setEditJob(job); setShowForm(true); }}
                      onDeleted={load}
                      onTolerance={onTolerance}
                    />
                  </div>
                ))
              )}
            </div>
          </section>
        </div>
        </>
        )}
      </main>

      <JobFormDialog open={showForm} onOpenChange={setShowForm} tail={editJob?.tail || selectedTail} ac={acByTail[editJob?.tail || selectedTail]} initial={editJob} onSaved={load} />
      <ScanDialog scan={scan} onClose={() => setScan(null)} tail={selectedTail} onImported={load} />
      <ToleranceDialog job={tolJob} onClose={() => setTolJob(null)} onApplied={load} />
    </div>
  );
}
